import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { AuditHistoryResponse } from 'app/model/audit/audit-history.model';
import { environment } from 'environments/environment';
import { Observable } from 'rxjs';

export interface AuditHistoryQueryParams {
    page: number;
    size: number;
    action?: 'CREATE' | 'UPDATE' | 'DELETE' | '';
    dateFrom?: string;
    dateTo?: string;
    scope?: string;
}

@Injectable({ providedIn: 'root' })
export class AuditHistoryService {
    private readonly apiUrl = `${environment.API_URL}/api/audit`;

    constructor(private http: HttpClient) {}

    getEntityHistory(entityType: string, entityId: string, query: AuditHistoryQueryParams): Observable<AuditHistoryResponse> {
        return this.http.get<AuditHistoryResponse>(`${this.apiUrl}/${encodeURIComponent(entityType)}/${encodeURIComponent(entityId)}`, {
            params: this.buildParams(query)
        });
    }

    getGlobalEntityHistory(entityType: string, query: AuditHistoryQueryParams): Observable<AuditHistoryResponse> {
        return this.http.get<AuditHistoryResponse>(`${this.apiUrl}/${encodeURIComponent(entityType)}`, {
            params: this.buildParams(query)
        });
    }

    getPatientTimeline(patientId: string, query: AuditHistoryQueryParams): Observable<AuditHistoryResponse> {
        const params = this.buildParams(query);
        if (query.scope && query.scope !== 'all' && query.scope !== 'patient') {
            params['scope'] = query.scope;
        }
        return this.http.get<AuditHistoryResponse>(`${this.apiUrl}/patient/${encodeURIComponent(patientId)}/timeline`, { params });
    }

    getEntryTimeline(entryId: string, query: AuditHistoryQueryParams): Observable<AuditHistoryResponse> {
        return this.http.get<AuditHistoryResponse>(`${this.apiUrl}/entry/${encodeURIComponent(entryId)}/timeline`, {
            params: this.buildParams(query)
        });
    }

    getAnalysisTimeline(query: AuditHistoryQueryParams): Observable<AuditHistoryResponse> {
        return this.http.get<AuditHistoryResponse>(`${this.apiUrl}/analysis/timeline`, {
            params: this.buildParams(query)
        });
    }

    getAnalysisResultsTimeline(requestId: string, query: AuditHistoryQueryParams): Observable<AuditHistoryResponse> {
        return this.http.get<AuditHistoryResponse>(`${this.apiUrl}/analysis-results/request/${encodeURIComponent(requestId)}/timeline`, {
            params: this.buildParams(query)
        });
    }

    getHospitalizationTimeline(hospitalizationId: string, query: AuditHistoryQueryParams): Observable<AuditHistoryResponse> {
        return this.http.get<AuditHistoryResponse>(`${this.apiUrl}/hospitalization/${encodeURIComponent(hospitalizationId)}/timeline`, {
            params: this.buildParams(query)
        });
    }

    getStockTimeline(query: AuditHistoryQueryParams): Observable<AuditHistoryResponse> {
        const params = this.buildParams(query);
        if (query.scope) {
            params['scope'] = query.scope;
        }
        return this.http.get<AuditHistoryResponse>(`${this.apiUrl}/stock/timeline`, { params });
    }

    private buildParams(query: AuditHistoryQueryParams): Record<string, string> {
        const params: Record<string, string> = {
            page: String(query.page ?? 0),
            size: String(query.size ?? 25)
        };
        if (query.action) params['action'] = query.action;
        if (query.dateFrom) params['dateFrom'] = query.dateFrom;
        if (query.dateTo) params['dateTo'] = query.dateTo;
        return params;
    }
}
